import { createContext, useContext, useEffect, useRef, useState } from "react";
import { auth } from "../firebase/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";

const AuthContext = createContext();
const SESSION_TIMEOUT = 120 * 60 * 1000; // 120 minutes

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timeoutRef = useRef(null);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log("User logged out due to inactivity.");
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const stopSessionTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    window.removeEventListener("mousemove", resetSessionTimer);
    window.removeEventListener("keydown", resetSessionTimer);
    window.removeEventListener("click", resetSessionTimer);
    window.removeEventListener("scroll", resetSessionTimer);
    window.removeEventListener("touchstart", resetSessionTimer);
  };

  const resetSessionTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, SESSION_TIMEOUT);
  };

  const startSessionTimer = () => {
    stopSessionTimer();
    resetSessionTimer();

    window.addEventListener("mousemove", resetSessionTimer);
    window.addEventListener("keydown", resetSessionTimer);
    window.addEventListener("click", resetSessionTimer);
    window.addEventListener("scroll", resetSessionTimer);
    window.addEventListener("touchstart", resetSessionTimer);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        startSessionTimer();
      } else {
        stopSessionTimer();
      }

      setLoading(false);
    });

    return () => {
      unsubscribe();
      stopSessionTimer();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, logout: handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
};